import { Provider } from '@vgtv/api-client';
import type { AppnexusSellerTag } from 'xandr';
import type Tracker from '@spt-tracking/pulse-sdk';

import { loadScript } from '../helpers/loadScript.js';
import { config } from '../config.js';
import { isAndroid, isHermesApp, isIOS } from '../utils/device.js';

import { getCookieData } from './appnexus/getCookieData.js';
import { getDeviceType } from './appnexus/getDeviceType.js';
import { getEnvironmentKeywords } from './appnexus/getEnvironmentKeywords.js';
import { getTracker } from './tracking/pulse/pulse.js';
import { consentManager } from './privacy.js';

const PLATFORM = isHermesApp ? 'app' : 'web';
const DEVICE_TYPE = getDeviceType();

let apnTagPromise: Promise<any>;

const getAppTypeKeyword = (): Record<string, string> => {
  if (
    config.provider === Provider.AB ||
    !isHermesApp ||
    (!isIOS && !isAndroid)
  ) {
    return {};
  }

  return {
    'no-sno-apptype': `${isIOS ? 'ios' : 'android'}_${DEVICE_TYPE}_hybrid`,
  } as const;
};

/**
 * Returns a Promise resolving with the Appnexus tag library, ensuring that it is loaded.
 *
 * @see {@link https://wiki.xandr.com/display/sdk/AST+API+Reference|XANDR Documentation}
 */
async function getApnTag() {
  if (apnTagPromise) {
    await apnTagPromise;
  }

  if (!window.apntag?.loaded) {
    window.apntag = window.apntag || {
      loaded: undefined,
      anq: [],
    };

    window.gdpr = window.gdpr || {};
    window.gdpr.allowHidingAds = true;

    apnTagPromise = Promise.all([
      loadScript('https://acdn.adnxs.com/ast/ast.js'),
      loadScript(
        'https://cogwheel.inventory.schibsted.io/prod/latest/gdpr-bundle.js'
      ),
    ]);

    await apnTagPromise;
  }

  // enables verbose logging locally
  window.apntag.debug = config.appnexus.debug || false;

  return window.apntag as AppnexusSellerTag;
}

const ppidSources = {
  [Provider.VGTV]: {
    envHash: 'SCHNO-EnvHash',
    userHash: 'SCHNO-UserHash',
  },
  [Provider.AB]: {
    envHash: 'SCHSE-EnvHash',
    userHash: 'SCHSE-UserHash',
  },
};

const getXandrPPIDs = async (
  provider: Provider,
  pulsePromise: Promise<Tracker>
) => {
  try {
    const pulse = await pulsePromise;
    if (pulse) {
      const { xandr: { ppId1: ppid1, ppId2: ppid2 } = {} } =
        await pulse.getAdvertisingIdentifiers();

      return { ppid1, ppid2 };
    }
    return {};
  } catch {
    console.warn('Unable to fetch PPIDs');
  }

  return {};
};

const generateXandrEids = (
  provider: Provider,
  ppid1?: string,
  ppid2?: string
) =>
  [
    { id: ppid1!, source: ppidSources[provider].userHash },
    { id: ppid2!, source: ppidSources[provider].envHash },
  ].filter(({ id }) => Boolean(id));

const generateXandrKeywords = (ppid1?: string, ppid2?: string) =>
  [
    { id: ppid1, keyword: 'aa-sch-schuserhash' },
    { id: ppid2, keyword: 'aa-sch-schenvhash' },
  ].reduce(
    (obj, { id, keyword }) => ({
      ...obj,
      [keyword]: id ? '1' : '0',
    }),
    {} as Record<string, string>
  );

/**
 * Defines and preloads Appnexus advert tags for the current device, setting up
 * global keywords and {@link PageOpts}.
 */
async function loadTags({
  keywords = {},
  loadCompanionAd = false,
}: {
  keywords?: Record<string, string | string[]>;
  loadCompanionAd?: boolean;
}) {
  const [apntag, { ppid1, ppid2 }, cmpConsent] = await Promise.all([
    getApnTag(),
    getXandrPPIDs(config.provider, getTracker()),
    consentManager.getConsentedToAll(),
  ]);

  const ppidKeywords = generateXandrKeywords(ppid1, ppid2);
  const eids = generateXandrEids(config.provider, ppid1, ppid2);

  apntag.clearRequest();

  const { adId, userId } = getCookieData();

  const countryPrefix = config.provider === Provider.VGTV ? 'no' : 'se';

  const environmentKeywords =
    config.provider === Provider.AB ? getEnvironmentKeywords(DEVICE_TYPE) : {};

  apntag.setPageOpts({
    ...config.appnexus.options,
    keywords: {
      ...config.appnexus.keywords,
      ...keywords,
      [`${countryPrefix}-generic-externalid_src`]: String(
        Number(Boolean(adId)) + Number(Boolean(userId))
      ),
      'aa-sch-supply_type': `${PLATFORM}_${DEVICE_TYPE}`,
      'aa-sch-cmp_advertising': cmpConsent ? '1' : '0',
      ...getAppTypeKeyword(),
      ...environmentKeywords,
      ...ppidKeywords,
    },
    user: {
      externalUid: Boolean(adId) ? adId : undefined,
      ...(eids.length > 0 && { userIds: [{ type: 'extendedIDs', eids }] }),
    },
  });

  const placements = config.appnexus.placements[PLATFORM]?.[DEVICE_TYPE] || [];

  placements.forEach((placement) => {
    if (placement.targetId === 'companionAd' && !loadCompanionAd) {
      return;
    }

    apntag.defineTag({ ...placement });
  });

  // use refresh instead of loadTags has us covered for the subsequent loads
  apntag.refresh();
}

/**
 * Actually shows an ad preloaded using {@link AppnexusSellerTag.loadTags}
 * @return {Promise} - resolves when ad is successfully shown, rejects when there's no ad available, or a load error has occurred
 */
function showTag(targetId: string) {
  return new Promise(async (resolve, reject) => {
    try {
      const apntag = await getApnTag();

      const removeListeners = () => {
        apntag.offEvent('adLoaded', targetId);
        apntag.offEvent('adNoBid', targetId);
        apntag.offEvent('adBadRequest', targetId);
        apntag.offEvent('adRequestFailure', targetId);
      };

      apntag.onEvent('adLoaded', targetId, (adData) => {
        resolve(adData);

        removeListeners();
      });

      apntag.onEvent('adNoBid', targetId, () => {
        reject('adNoBid');

        removeListeners();
      });

      apntag.onEvent('adBadRequest', targetId, () => {
        reject('adBadRequest');

        removeListeners();
      });

      apntag.onEvent('adRequestFailure', targetId, () => {
        reject('adRequestFailure');

        removeListeners();
      });

      apntag.showTag(targetId);
    } catch (err) {
      reject(err);
    }
  });
}

const modifyCompanionAd = (targetId: string, videoCreativeId: string) => {
  return new Promise(async (resolve, reject) => {
    if (config.provider === Provider.VGTV) {
      reject();

      return;
    }

    try {
      const apntag = await getApnTag();

      const removeListeners = () => {
        apntag.offEvent('adLoaded', targetId);
        apntag.offEvent('adNoBid', targetId);
        apntag.offEvent('adBadRequest', targetId);
        apntag.offEvent('adRequestFailure', targetId);
      };

      apntag.onEvent('adLoaded', targetId, (adData) => {
        resolve(adData);

        removeListeners();
      });

      apntag.onEvent('adNoBid', targetId, () => {
        reject('adNoBid');

        removeListeners();
      });

      apntag.onEvent('adBadRequest', targetId, () => {
        reject('adBadRequest');

        removeListeners();
      });

      apntag.onEvent('adRequestFailure', targetId, () => {
        reject('adRequestFailure');

        removeListeners();
      });

      apntag.modifyTag(targetId, {
        keywords: {
          'se-generic-adformat': 'companion',
          'se-generic-instream-creative-id': videoCreativeId,
        },
      });

      apntag.refresh(targetId);
    } catch (err) {
      reject(err);
    }
  });
};

export { loadTags, showTag, modifyCompanionAd };
